import React from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { get } from "../../constants/theme";
import Fade from "../Animations/Fade";
import Box from "../Box/Box";
import MotionBox from "../Box/MotionBox";
import AppMenuItem from "../AppMenuItem";
import { GatsbyImage } from "gatsby-plugin-image";
import { NavigationQuery } from "../../types/components/navigationMenu";

type RouteItem = NavigationQuery["translationsJson"]["components"]["navigation"]["routes"][number];
import { SmallTextWrapper } from "../Styled/Text";

const NavigationModal = () => {
  const [activePicture, setActivePicture] = React.useState<string | null>(null);
  const { langVersLinks, routes } = useStaticQuery<NavigationQuery>(graphql`
    query NavigationQuery {
      translationsJson {
        components {
          navigation {
            langVersLinks {
              ru
              en
            }
            routes {
              href
              id
              image {
                relativePath
                childImageSharp {
                  gatsbyImageData
                }
              }
              imageAlt
              order
              title
              disabled
              subRoutes {
                id
                title
                href
                disabled
              }
            }
          }
        }
      }
    }
  `).translationsJson.components.navigation;

  const changeActivePicture = React.useCallback((id: string) => {
    setActivePicture(id);
  }, []);

  const unsetActivePicture = React.useCallback(() => {
    setActivePicture(null);
  }, []);

  React.useEffect(() => {
    // if (isIOS) document.body.style.position = "fixed";
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "auto";
      // if (isIOS) document.body.style.position = "";
    };
  }, []);

  if (typeof document === "undefined") return null;

  return (
    <Fade>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
          zIndex: 100,
          backgroundColor: get("palette.background.gold"),
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            paddingTop: ["80px", "80px", "80px", "100px"],
            paddingBottom: ["40px", "40px", "40px", "60px"],
            boxSizing: "border-box",
          }}
        >
          <Box
            as="nav"
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textTransform: "uppercase",
              margin: "0 15%",
              "& > :last-child": {
                marginBottom: 0,
              },
            }}
          >
            {routes.map((r: RouteItem) => (
              <Box
                key={r.id}
                sx={{
                  marginBottom: [0, 0, 0, "28px"],
                  "&  a": {
                    color: get("palette.text.dark_gold"),
                  },
                }}
              >
                <AppMenuItem
                  disabled={r.disabled}
                  id={r.id}
                  to={r.href}
                  handleHoverStart={changeActivePicture}
                  handleHoverEnds={unsetActivePicture}
                >
                  {r.title}
                </AppMenuItem>
                {r.subRoutes && r.subRoutes.length > 0 && (
                  <Box
                    sx={{
                      borderLeftWidth: "1px",
                      borderLeftStyle: "solid",
                      borderLeftColor: get("palette.text.dark_gold"),
                      marginLeft: 0,
                      marginTop: ["4px", "4px", "12px", "12px"],
                      marginBottom: ["24px", "24px", "20px", "20px"],
                      paddingLeft: "30px",
                      display: "flex",
                      flexDirection: "column",
                      textTransform: "none",
                      gap: ["12px", "12px", "1px", "1px"]
                    }}
                  >
                    {r.subRoutes.map((sub) => (
                      <Box
                        key={sub.id}
                        as={sub.disabled ? "span" : (Link as any)}
                        {...(sub.disabled ? {} : { to: sub.href })}
                        style={{ textAlign: "left" }}
                        sx={{
                          display: "block",
                          fontFamily: "custom_42266",
                          fontWeight: 400,
                          fontSize: ["18px", "18px", "22px", "22px"],
                          lineHeight: ["28px", "28px", "36px", "36px"],
                          color: get("palette.text.dark_gold"),
                          opacity: sub.disabled ? 0.5 : 1,
                          cursor: sub.disabled ? "default" : "pointer",
                          pointerEvents: sub.disabled ? "none" : undefined,
                          textTransform: "none",
                          textDecoration: "none",
                        }}
                      >
                        {sub.title}
                      </Box>
                    ))}
                  </Box>
                )}
                {r.image && (
                  <MotionBox
                    sx={{
                      position: "absolute",
                      display: ["none", "block", "block", "block"],
                      right: 0,
                      top: "50%",
                      height: "100%",
                      width: ["200px", "200px", "223px", "380px"],
                      maxWidth: ["200px", "200px", "223px", "max(223px, 30%)"],
                      transform: "translateY(-50%)",
                    }}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: activePicture === r.id ? 1 : 0,
                      transition: { duration: 0.3 },
                    }}
                  >
                    <Box
                      as={GatsbyImage}
                      alt=""
                      objectFit="contain"
                      image={r.image.childImageSharp.gatsbyImageData as any}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </MotionBox>
                )}
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: ["center", "center", "flex-end", "flex-end"],
              flexDirection: "row",
              gap: "2rem",
              color: get("palette.text.dark_gold"),
              paddingRight: ["0", "0", "5%", "5%"],
              marginTop: ["40px", "40px", "40px", "60px"],
              flexShrink: 0,
            }}
          >
          <SmallTextWrapper
            sx={{
              cursor: "pointer",
            }}
            as={"a"}
            href={langVersLinks.ru}
          >
            RU
          </SmallTextWrapper>
          <SmallTextWrapper
            sx={{
              cursor: "pointer",
            }}
            as={"a"}
            href={langVersLinks.en}
          >
            EN
          </SmallTextWrapper>
        </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default React.memo(NavigationModal);
